import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api, { getImageUrl } from '@/lib/api';
import { MentionTextarea } from '../components/MentionTextarea';
import { LockablePrompt } from '../components/LockablePrompt';
import { FrameRefPicker } from '../components/FrameRefPicker';
import { MediaPicker } from '../components/MediaPicker';
import { useJobs } from '@/contexts/JobsContext';
import { useRates } from '@/contexts/RatesContext';
import { estimateVideoCost, estimateFrameCost, formatCost, failureHint } from '@/lib/generation';
import {
  ArrowLeft, ArrowRight, Check, ChevronRight, Film, Loader2,
  Camera, Mic, Video, MapPin, Wand2, Users, Sparkles, Upload,
  ImageIcon, Pencil, X, Plus, Volume2, FileText, Copy, Play,
  Save, Shirt, Lock, Unlock, Grid3x3, Download, Key, Eye, EyeOff,
  ExternalLink, UserX, ChevronLeft, RotateCcw, Link2, AlertCircle, AlertTriangle, Maximize2, Languages
} from 'lucide-react';
import { ImageLightbox } from '@/components/ImageLightbox';
import { FrameEditor } from '@/components/FrameEditor';
import { VideoResult } from '@/components/VideoResult';
import { NativeVoiceDialogueNotice, NativeVoiceVideoNotice, NativeLinePreviewButton, getNativeSpeakers } from '@/components/NativeVoice';
import { KlingIdentityNotice } from '@/components/KlingIdentity';
import { VISUAL_STYLES, PIXEL_BIT_DEPTHS } from '@/components/StyleSelector';
import { SceneLocations } from '@/components/SceneLocations';
import { EntityPicker } from '@/components/EntityPicker';
import { LinkExistingPicker } from '@/components/LinkExistingPicker';
import { SceneLanguages } from '@/components/SceneLanguages';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PreflightChecklist, ReferenceStrip } from '@/components/PreflightChecklist';

const WIZARD_STEPS = [
  { key: 'characters', label: 'Characters', icon: Users },
  { key: 'scene', label: 'Scene & Location', icon: Film },
  { key: 'frames', label: 'Frames', icon: Camera },
  { key: 'audio', label: 'Audio', icon: Mic },
  { key: 'production', label: 'Production', icon: Video },
];

const KEY_LABELS = {
  openai_api_key: { label: 'OpenAI API Key', url: 'https://platform.openai.com/api-keys' },
  gemini_api_key: { label: 'Google Gemini API Key', url: 'https://aistudio.google.com/apikey' },
  kling_api_key: { label: 'Kling API Key', url: 'https://kling.ai/dev/api-key' },
  google_ai_api_key: { label: 'Google AI (Veo) API Key', url: 'https://aistudio.google.com/apikey' },
  seedance_api_key: { label: 'Seedance (BytePlus) API Key', url: 'https://seedance.ai' },
  elevenlabs_api_key: { label: 'ElevenLabs API Key', url: 'https://elevenlabs.io' },
};

// Estimated generation times (seconds) per provider per duration tier
const PROVIDER_EST_TIMES = {
  kling:    { base: 55, perSec: 7 },   // ~55s + 7s per video-second
  veo:      { base: 30, perSec: 4 },   // ~30s + 4s per video-second
  seedance: { base: 50, perSec: 8 },   // ~50s + 8s per video-second
};

export default function ProductionMode() {
  const { projectId, sceneId: singleSceneId } = useParams();
  const singleMode = !!singleSceneId;
  const [urlParams] = useSearchParams();
  const navigate = useNavigate();
  const [showAddChar, setShowAddChar] = useState(false);
  const [linkChar, setLinkChar] = useState(null);
  const [showLanguages, setShowLanguages] = useState(false);
  const [sceneLocations, setSceneLocations] = useState([]);
  const { refresh: refreshJobs } = useJobs();
  const { rates } = useRates();
  const [project, setProject] = useState(null);
  const [savingIdentityLock, setSavingIdentityLock] = useState(false);
  const [scenes, setScenes] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [currentScene, setCurrentScene] = useState(null);
  const [loading, setLoading] = useState(true);
  const [identifyingScene, setIdentifyingScene] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const simple = project?.creation_mode === 'simple';
  const visibleSteps = simple ? WIZARD_STEPS.filter(s => ['frames', 'audio', 'production'].includes(s.key)) : WIZARD_STEPS;
  useEffect(() => { if (simple && wizardStep < 2) setWizardStep(2); }, [simple, wizardStep]);
  const [isFinalScene, setIsFinalScene] = useState(false);

  // Character step
  const [charIndex, setCharIndex] = useState(0);
  const [imageProvider, setImageProvider] = useState('gemini');
  const [genCharImgId, setGenCharImgId] = useState(null);
  const charUploadRefs = useRef({});
  const [charLocked, setCharLocked] = useState({});
  const [charExtras, setCharExtras] = useState({});
  const [showUnlockWarning, setShowUnlockWarning] = useState(null);

  // Scene + Location step
  const [sceneForm, setSceneForm] = useState({ scene_prompt: '', setting: '', time_of_day: '', camera_movement: '', mood: '' });
  const [savingScene, setSavingScene] = useState(false);

  // Character outfit refs
  const [charOutfits, setCharOutfits] = useState({});
  const [genCharRefLoading, setGenCharRefLoading] = useState(null);
  const [suggestingOutfit, setSuggestingOutfit] = useState(null);
  const [projectRefs, setProjectRefs] = useState({ location_refs: [], character_refs: [] });
  const [showRefPicker, setShowRefPicker] = useState(null);

  // Frames step
  const [genFrameLoading, setGenFrameLoading] = useState(null);
  const [actionsForm, setActionsForm] = useState({ start_action: '', end_action: '' });
  const [useOpeningRef, setUseOpeningRef] = useState(false);
  const startFrameUploadRef = useRef(null);
  const endFrameUploadRef = useRef(null);
  const [uploadingFrame, setUploadingFrame] = useState(null);

  // Audio step
  const [dialogueLines, setDialogueLines] = useState([]);
  const [audioDirection, setAudioDirection] = useState('');
  const [elevenLabsVoices, setElevenLabsVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [genTtsLoading, setGenTtsLoading] = useState(false);
  const [playingPreview, setPlayingPreview] = useState(null);
  const previewAudioRef = useRef(null);

  // Production step
  const [videoPrompt, setVideoPrompt] = useState('');
  const [editingPrompt, setEditingPrompt] = useState(false);
  // A scene with no video prompt opens straight into the editor — no unlock step for a first write.
  useEffect(() => { if (currentScene && !(currentScene.video_prompt || '').trim()) setEditingPrompt(true); else setEditingPrompt(false); }, [currentScene?.id, currentScene?.video_prompt]); // eslint-disable-line react-hooks/exhaustive-deps

  const [genPromptLoading, setGenPromptLoading] = useState(false);
  const [genVideoLoading, setGenVideoLoading] = useState(false);
  const [videoProvider, setVideoProvider] = useState('kling');
  const [videoDuration, setVideoDuration] = useState(5);
  const [videoResolution, setVideoResolution] = useState('720p');
  const [editingPrompts, setEditingPrompts] = useState({});
  const setPromptEditing = (name) => (v) => setEditingPrompts(p => ({ ...p, [name]: v }));
  const [mediaPicker, setMediaPicker] = useState(null);
  const [refUploading, setRefUploading] = useState(null);
  const handleUploadFrameRef = async (e, which) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setRefUploading(which);
    try {
      const res = await api.post(`/projects/${projectId}/scenes/${currentScene.id}/upload-frame-ref/${which}`, fd);
      setCurrentScene(res.data); setScenes(prev => prev.map(s => s.id === currentScene.id ? res.data : s));
      toast.success('Reference image added — generate the frame to use it');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setRefUploading(null); if (e.target) e.target.value = ''; }
  };
  const handleRemoveFrameRef = async (which) => {
    const res = await api.delete(`/projects/${projectId}/scenes/${currentScene.id}/frame-ref/${which}`);
    setCurrentScene(res.data); setScenes(prev => prev.map(s => s.id === currentScene.id ? res.data : s));
  };
  const handleUseMedia = async (item) => {
    if (!mediaPicker) return;
    const res = await api.post(`/projects/${projectId}/scenes/${currentScene.id}/use-media`, { slot: mediaPicker.slot, path: item.path });
    setCurrentScene(res.data); setScenes(prev => prev.map(s => s.id === currentScene.id ? res.data : s));
    toast.success('Media applied');
  };
  const [videoStatus, setVideoStatus] = useState(null);
  const [frameError, setFrameError] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [copied, setCopied] = useState(false);
  const videoPollingRef = useRef(null);
  const pollingSceneIdRef = useRef(null);
  const [genStartTime, setGenStartTime] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const elapsedTimerRef = useRef(null);

  // Missing key modal
  const [missingKeys, setMissingKeys] = useState(null);
  useEffect(() => { if (projectId) api.get(`/projects/${projectId}/locations`).then(r => setSceneLocations(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, [projectId, currentScene?.location_id, currentScene?.closing_location_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [inlineKeyValues, setInlineKeyValues] = useState({});
  const [showInlineKeys, setShowInlineKeys] = useState({});
  const [savingInlineKey, setSavingInlineKey] = useState(false);
  const [flushingJobs, setFlushingJobs] = useState(false);

  const handleGenError = (err, fallbackMsg = 'Generation failed') => {
    const detail = err.response?.data?.detail || '';
    if (typeof detail === 'string' && detail.startsWith('MISSING_KEY:')) {
      const [keysPart, msgPart] = detail.replace('MISSING_KEY:', '').split('|');
      setMissingKeys({ keys: keysPart.split(','), message: msgPart || 'API key not configured' });
      setInlineKeyValues({}); setShowInlineKeys({});
      return true;
    }
    toast.error(detail || fallbackMsg);
    return false;
  };

  const handleSaveInlineKeys = async () => {
    const keysToSave = {};
    Object.entries(inlineKeyValues).forEach(([k, v]) => { if (v?.trim()) keysToSave[k] = v.trim(); });
    if (Object.keys(keysToSave).length === 0) { toast.error('Paste at least one API key'); return; }
    setSavingInlineKey(true);
    try {
      const teamsRes = await api.get('/teams');
      const activeTeam = teamsRes.data.find(t => t.is_active);
      if (!activeTeam) { toast.error('No active team'); return; }
      await api.put(`/teams/${activeTeam.id}/api-keys`, keysToSave);
      toast.success('API key saved! Try generating again.');
      setMissingKeys(null); setInlineKeyValues({});
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save key'); }
    finally { setSavingInlineKey(false); }
  };

  const [cancellingVideo, setCancellingVideo] = useState(false);
  const handleCancelVideo = async () => {
    const sceneId = pollingSceneIdRef.current || currentScene?.id;
    if (!sceneId) return;
    setCancellingVideo(true);
    try {
      const res = await api.post(`/projects/${projectId}/scenes/${sceneId}/cancel-video`);
      if (videoPollingRef.current) { clearTimeout(videoPollingRef.current); videoPollingRef.current = null; }
      pollingSceneIdRef.current = null;
      setGenVideoLoading(false); stopElapsedTimer();
      setVideoStatus(res.data);
      refreshJobs();
      toast.info(res.data?.error || 'Generation cancelled');
    } catch (err) {
      if (err.response?.status === 409) { toast.info(err.response.data.detail); pollVideoStatus(sceneId); }
      else toast.error(err.response?.data?.detail || 'Could not cancel');
    } finally { setCancellingVideo(false); }
  };

  const handleFlushJobs = async () => {
    if (!window.confirm('Reset all stuck/processing video jobs for this project? This will mark them as failed so you can retry.')) return;
    setFlushingJobs(true);
    try {
      const res = await api.post(`/projects/${projectId}/flush-stuck-jobs`);
      toast.success(res.data.message || 'Jobs reset!');
      setGenVideoLoading(false); setVideoStatus(null);
      if (videoPollingRef.current) { clearTimeout(videoPollingRef.current); videoPollingRef.current = null; }
      stopElapsedTimer();
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset jobs'); }
    finally { setFlushingJobs(false); }
  };

  // ── Data Loading ──
  const loadData = useCallback(async () => {
    try {
      const [projRes, scenesRes, charsRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/scenes`),
        api.get(`/projects/${projectId}/characters`)
      ]);
      setProject(projRes.data);
      setScenes(scenesRes.data);
      setCharacters(charsRes.data);
      projLoadedRef.current = true;
      if (scenesRes.data.length > 0) {
        const single = singleSceneId ? scenesRes.data.find(sc => sc.id === singleSceneId) : null;
        if (singleSceneId && !single) { toast.error('Scene not found'); navigate(`/project/${projectId}?tab=scenes`); return; }
        const idx = projRes.data.current_scene_index != null ? projRes.data.current_scene_index : scenesRes.data.length - 1;
        const scene = single || scenesRes.data[Math.min(idx, scenesRes.data.length - 1)];
        loadSceneIntoState(scene, charsRes.data);
        const stepParam = parseInt(urlParams.get('step'), 10);
        if (!Number.isNaN(stepParam) && stepParam >= 0 && stepParam <= 4) setWizardStep(stepParam);
        else if (!single && projRes.data.wizard_step != null) setWizardStep(projRes.data.wizard_step);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to load project:', err);
      setLoading(false);
      toast.error('Failed to load project');
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); return () => { if (videoPollingRef.current) clearTimeout(videoPollingRef.current); if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current); if (previewAudioRef.current) previewAudioRef.current.pause(); }; }, [loadData]);

  useEffect(() => { if ((wizardStep === 0 || wizardStep === 1) && currentScene) loadProjectRefs(); }, [wizardStep, currentScene?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist wizard step + selected scene so the user resumes exactly where they left off
  const projLoadedRef = useRef(false);
  useEffect(() => {
    if (!projLoadedRef.current || !currentScene || singleMode) return;
    const idx = scenes.findIndex(s => s.id === currentScene.id);
    api.put(`/projects/${projectId}`, { wizard_step: wizardStep, ...(idx >= 0 ? { current_scene_index: idx } : {}) }).catch(() => {});
  }, [wizardStep, currentScene?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scenes with locked native voices default to Kling (the only provider that can use them)
  useEffect(() => {
    if (currentScene && getNativeSpeakers(currentScene.dialogue_lines, characters).withVoice.length > 0) { setVideoProvider('kling'); setVideoDuration(5); }
  }, [currentScene?.id, characters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSceneIntoState = (scene, chars) => {
    // Switching scenes: clear any leftover video-generation state from the previous scene
    if (videoPollingRef.current) { clearTimeout(videoPollingRef.current); videoPollingRef.current = null; }
    pollingSceneIdRef.current = null;
    setGenVideoLoading(false); setVideoStatus(null); setFrameError(null);
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    setGenStartTime(null);
    setCurrentScene(scene);
    setCharIndex(0);
    setSceneForm({ scene_prompt: scene.scene_prompt || '', setting: scene.setting || '', time_of_day: scene.time_of_day || '', camera_movement: scene.camera_movement || '', mood: scene.mood || '' });
    setActionsForm({ start_action: scene.start_action || '', end_action: scene.end_action || '' });
    setDialogueLines(scene.dialogue_lines || []);
    setAudioDirection(scene.audio_direction || '');
    setVideoPrompt(scene.video_prompt || '');
    const outfits = {};
    (scene.character_scene_refs || []).forEach(r => { outfits[r.character_id] = r.outfit_description || ''; });
    setCharOutfits(outfits);
    // Set locked state for characters that already have images
    const locked = {};
    const extras = {};
    const allChars = chars || characters;
    (scene.character_ids || []).forEach(cid => {
      const c = allChars.find(ch => ch.id === cid);
      if (c?.image_storage_path) locked[cid] = true;
      if (c?.is_extra || scene.extra_character_ids?.includes(cid)) extras[cid] = true;
    });
    setCharLocked(locked);
    setCharExtras(extras);
    // Set initial wizard step based on progress
    if (scene.video_path) setWizardStep(4);
    else if (scene.first_frame_path || scene.last_frame_path) setWizardStep(2);
    else if (scene.location_ref_path) setWizardStep(1);
    else setWizardStep(0);
    // Sync video-generation state with the backend for THIS scene
    (async () => {
      try {
        const st = await api.get(`/projects/${projectId}/scenes/${scene.id}/video-status`, { params: { _t: Date.now() }, timeout: 10000 });
        if (st.data?.status === 'processing') {
          setWizardStep(4);
          setGenVideoLoading(true);
          setVideoStatus(st.data);
          const t0 = st.data.created_at ? new Date(st.data.created_at).getTime() : Date.now();
          if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
          setGenStartTime(t0); setElapsedSec(Math.max(0, Math.floor((Date.now() - t0) / 1000)));
          elapsedTimerRef.current = setInterval(() => setElapsedSec(Math.max(0, Math.floor((Date.now() - t0) / 1000))), 1000);
          pollVideoStatus(scene.id);
        } else if (st.data?.status === 'complete' || st.data?.status === 'failed') {
          setVideoStatus(st.data);
        }
      } catch (e) { console.warn('Video status check skipped:', e?.message); }
    })();
    // Surface the reason a frame generation failed (persists across navigation)
    (async () => {
      try {
        const fs = await api.get(`/projects/${projectId}/scenes/${scene.id}/frame-gen-status`, { params: { _t: Date.now() } });
        setFrameError(fs.data?.status === 'failed' ? (fs.data.error || 'Frame generation failed') : null);
      } catch { /* ignore */ }
    })();
  };

  const updateProjectSetting = async (patch) => {
    try { const r = await api.put(`/projects/${projectId}`, patch); setProject(p => ({ ...p, ...r.data })); toast.success('Project setting saved'); }
    catch { toast.error('Could not save project setting'); }
  };

  const updateCurrentScene = async (updates) => {
    if (!currentScene) return;
    try {
      const res = await api.put(`/projects/${projectId}/scenes/${currentScene.id}`, updates);
      setCurrentScene(res.data);
      setScenes(prev => prev.map(s => s.id === currentScene.id ? res.data : s));
      return res.data;
    } catch { toast.error('Failed to save'); }
  };

  const sceneCharacters = characters.filter(c => (currentScene?.character_ids || []).includes(c.id));
  const completedScenes = scenes.filter(s => s.video_path).length;
  const sceneNumber = scenes.findIndex(s => s.id === currentScene?.id) + 1;
  const currentChar = sceneCharacters[charIndex];

  // ── Scene Identification ──
  const handleIdentifyNextScene = async () => {
    setIdentifyingScene(true); setWizardStep(0);
    try {
      // Trigger background scene identification (returns immediately)
      await api.post(`/projects/${projectId}/identify-next-scene`, {}, { timeout: 15000 });
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 120; // 10 min
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const statusRes = await api.get(`/projects/${projectId}/scene-identification-status`, { params: { _t: Date.now() } });
          const job = statusRes.data;
          if (job.status === 'complete' && job.scene) {
            setIsFinalScene(job.is_final_scene);
            setScenes(prev => [...prev, job.scene]);
            const charsRes = await api.get(`/projects/${projectId}/characters`);
            setCharacters(charsRes.data);
            loadSceneIntoState(job.scene, charsRes.data);
            toast.success(`Scene ${job.scene_number} identified!`);
            return;
          } else if (job.status === 'failed') {
            toast.error(job.error || 'Failed to identify scene');
            return;
          }
        } catch {}
      }
      toast.error('Scene identification timed out. Please try again.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setIdentifyingScene(false); }
  };

  // ── Character Handlers ──
  const handleGenCharImage = async (charId) => {
    setGenCharImgId(charId);
    try {
      await api.post(`/projects/${projectId}/characters/${charId}/generate-image`, { prompt: '', image_provider: imageProvider }, { timeout: 15000 });
      // Poll for completion
      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const statusRes = await api.get(`/projects/${projectId}/characters/${charId}/image-gen-status`, { params: { _t: Date.now() } });
          if (statusRes.data.status === 'complete' && statusRes.data.character) {
            setCharacters(prev => prev.map(c => c.id === charId ? statusRes.data.character : c));
            setCharLocked(prev => ({ ...prev, [charId]: true }));
            toast.success('Image generated!');
            return;
          } else if (statusRes.data.status === 'failed') {
            toast.error(statusRes.data.error || 'Failed to generate');
            return;
          }
        } catch {}
      }
      toast.error('Image generation timed out');
    } catch (err) { handleGenError(err, 'Failed to generate'); }
    finally { setGenCharImgId(null); }
  };

  const handleUploadCharImage = async (charId, file) => {
    if (!file) return;
    setGenCharImgId(charId);
    try {
      const formData = new FormData(); formData.append('file', file);
      const res = await api.post(`/projects/${projectId}/characters/${charId}/upload-image`, formData);
      setCharacters(prev => prev.map(c => c.id === charId ? res.data : c));
      setCharLocked(prev => ({ ...prev, [charId]: true }));
      toast.success('Image uploaded!');
    } catch { toast.error('Failed to upload'); }
    finally { setGenCharImgId(null); }
  };

  const handleUpdateCharDescription = async (charId, description) => {
    try {
      await api.put(`/projects/${projectId}/characters/${charId}`, { description });
      setCharacters(prev => prev.map(c => c.id === charId ? { ...c, description } : c));
    } catch { toast.error('Failed to save'); }
  };
  const handleSaveCharDescription = async (charId, description) => {
    try {
      await api.put(`/projects/${projectId}/characters/${charId}`, { description });
      setCharacters(prev => prev.map(c => c.id === charId ? { ...c, description } : c));
      toast.success('Description saved');
    } catch { toast.error('Failed to save'); }
  };

  const handleSuggestOutfit = async (charId) => {
    setSuggestingOutfit(charId);
    try {
      const res = await api.post(`/projects/${projectId}/scenes/${currentScene.id}/suggest-outfit`, { character_id: charId }, { timeout: 30000 });
      setCharOutfits(prev => ({ ...prev, [charId]: res.data.suggestion }));
      toast.success('Outfit suggested!');
    } catch { toast.error('Failed to suggest'); }
    finally { setSuggestingOutfit(null); }
  };

  const handleGenerateCharRef = async (charId) => {
    setGenCharRefLoading(charId);
    try {
      const res = await api.post(`/projects/${projectId}/scenes/${currentScene.id}/generate-character-ref`,
        { character_id: charId, outfit_description: charOutfits[charId] || '', image_provider: imageProvider }, { timeout: 120000 });
      setCurrentScene(res.data); setScenes(prev => prev.map(s => s.id === currentScene.id ? res.data : s));
      toast.success('Outfit reference generated!');
    } catch (err) { handleGenError(err, 'Failed'); }
    finally { setGenCharRefLoading(null); }
  };

  const loadProjectRefs = async () => { try { const res = await api.get(`/projects/${projectId}/reference-images`); setProjectRefs(res.data); } catch {} };

  const handleSelectCharRef = async (charId, charName, refPath, outfitDesc) => {
    const existing = currentScene?.character_scene_refs || [];
    const newRefs = existing.filter(r => r.character_id !== charId);
    newRefs.push({ character_id: charId, character_name: charName, ref_image_path: refPath, outfit_description: outfitDesc });
    try {
      const res = await api.put(`/projects/${projectId}/scenes/${currentScene.id}`, { character_scene_refs: newRefs });
      setCurrentScene(res.data); setScenes(prev => prev.map(s => s.id === currentScene.id ? res.data : s));
      setCharOutfits(prev => ({ ...prev, [charId]: outfitDesc })); setShowRefPicker(null);
      toast.success('Previous outfit applied!');
    } catch { toast.error('Failed'); }
  };

  // ── Location ──

  // ── Frames ──
  const handleSaveActions = async () => { await updateCurrentScene(actionsForm); };
  const handleGenerateFrame = async (type) => {
    setGenFrameLoading(type);
    setFrameError(null);
    try {
      const sceneId = currentScene?.id;
      // Save current action text before generating so backend uses latest prompts
      await updateCurrentScene({
        start_action: currentScene?.start_action || '',
        end_action: currentScene?.end_action || '',
      });
      const useRef = type === 'last' && useOpeningRef ? '&use_opening_ref=true' : '';
      await api.post(`/projects/${projectId}/scenes/${sceneId}/generate-frames?frame=${type}&image_provider=${imageProvider}${useRef}`, {}, { timeout: 15000 });
      refreshJobs();
      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        try {
          const statusRes = await api.get(`/projects/${projectId}/scenes/${sceneId}/frame-gen-status`, { params: { _t: Date.now() } });
          if (statusRes.data.status === 'complete' && statusRes.data.scene) {
            setCurrentScene(statusRes.data.scene); setScenes(prev => prev.map(s => s.id === sceneId ? statusRes.data.scene : s));
            setFrameError(null);
            toast.success(`${type === 'first' ? 'Opening' : 'Closing'} frame generated!`);
            return;
          } else if (statusRes.data.status === 'failed') {
            setFrameError(statusRes.data.error || 'Frame generation failed');
            toast.error(statusRes.data.error || 'Failed');
            return;
          }
        } catch {}
      }
      toast.error('Frame generation timed out');
    } catch (err) { handleGenError(err, 'Failed'); }
    finally { setGenFrameLoading(null); refreshJobs(); }
  };
  const handleUploadFrame = async (e, type) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingFrame(type);
    try {
      const formData = new FormData(); formData.append('file', file);
      const endpoint = type === 'first' ? 'upload-start-frame' : 'upload-end-frame';
      const res = await api.post(`/projects/${projectId}/scenes/${currentScene.id}/${endpoint}`, formData);
      setCurrentScene(res.data); setScenes(prev => prev.map(s => s.id === currentScene.id ? res.data : s));
      toast.success('Frame uploaded!');
    } catch { toast.error('Failed to upload'); }
    finally { setUploadingFrame(null); }
  };
  const handleDownloadFrame = async (path, name) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click();
      document.body.removeChild(a); window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  // ── Seedance reference clips (per scene) ──
  const refVideoUploadRef = useRef(null);
  const refAudioUploadRef = useRef(null);
  const [uploadingRef, setUploadingRef] = useState(null);
  const handleUploadReference = async (e, kind) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadingRef(kind);
    try {
      const formData = new FormData(); formData.append('file', file);
      const endpoint = kind === 'video' ? 'upload-reference-video' : 'upload-reference-audio';
      const res = await api.post(`/projects/${projectId}/scenes/${currentScene.id}/${endpoint}`, formData);
      setCurrentScene(res.data); setScenes(prev => prev.map(s => s.id === currentScene.id ? res.data : s));
      toast.success(`Reference ${kind} added!`);
    } catch { toast.error(`Reference ${kind} upload failed`); }
    finally {
      setUploadingRef(null);
      if (kind === 'video' && refVideoUploadRef.current) refVideoUploadRef.current.value = '';
      if (kind === 'audio' && refAudioUploadRef.current) refAudioUploadRef.current.value = '';
    }
  };
  const handleDeleteReference = async (kind) => {
    try {
      const endpoint = kind === 'video' ? 'reference-video' : 'reference-audio';
      const res = await api.delete(`/projects/${projectId}/scenes/${currentScene.id}/${endpoint}`);
      setCurrentScene(res.data); setScenes(prev => prev.map(s => s.id === currentScene.id ? res.data : s));
    } catch { toast.error('Failed to remove'); }
  };

  // Get previous scene's closing frame (for continuity linking)
  const prevScene = scenes.length > 0 && currentScene
    ? scenes.find(s => s.scene_order === (currentScene.scene_order || 0) - 1)
    : null;
  const prevClosingFrame = prevScene?.last_frame_path;

  const handleUsePrevClosingFrame = async () => {
    if (!prevClosingFrame) return;
    try {
      await updateCurrentScene({ first_frame_path: prevClosingFrame });
      toast.success('Linked to previous scene\'s closing frame');
    } catch { toast.error('Failed to link frame'); }
  };

  // ── Audio ──
  const handleSaveAudio = async () => { await updateCurrentScene({ audio_direction: audioDirection, dialogue_lines: dialogueLines }); };
  const addDialogueLine = () => {
    const defaultChar = sceneCharacters[0];
    setDialogueLines(prev => [...prev, { id: Date.now().toString(), character_id: defaultChar?.id || '', character_name: defaultChar?.name || '', voice_id: '', voice_name: '', elevenlabs_voice_id: defaultChar?.voice_id || '', elevenlabs_voice_name: defaultChar?.voice_name || '', text: '', start_time: 0, tts_path: '', tts_duration: '', tts_provider: '' }]);
  };
  const updateDialogueLine = (idx, field, value) => {
    setDialogueLines(prev => prev.map((line, i) => {
      if (i !== idx) return line;
      const updated = { ...line, [field]: value };
      if (field === 'character_id') {
        const char = characters.find(c => c.id === value);
        if (char) {
          updated.character_name = char.name;
          if (char.voice_id) { updated.elevenlabs_voice_id = char.voice_id; updated.elevenlabs_voice_name = char.voice_name || ''; }
        }
      }
      return updated;
    }));
  };
  const fetchElevenLabsVoices = async () => {
    if (elevenLabsVoices.length > 0) return;
    setLoadingVoices(true);
    try { const res = await api.get('/elevenlabs/voices'); setElevenLabsVoices(res.data.voices || []); }
    catch (err) { handleGenError(err, 'Failed to load voices'); }
    finally { setLoadingVoices(false); }
  };
  const handleGenerateElevenLabsTts = async () => {
    setGenTtsLoading(true);
    try {
      await handleSaveAudio();
      const res = await api.post(`/projects/${projectId}/scenes/${currentScene.id}/generate-elevenlabs-tts`, {}, { timeout: 120000 });
      setDialogueLines(res.data.dialogue_lines || dialogueLines);
      const successes = (res.data.results || []).filter(r => r.status === 'success').length;
      if (successes > 0) toast.success(`${successes} line(s) generated!`);
      else toast.error('Assign ElevenLabs voices first.');
    } catch (err) { handleGenError(err, 'TTS failed'); }
    finally { setGenTtsLoading(false); }
  };
  const playVoicePreview = (voiceId) => {
    const voice = elevenLabsVoices.find(v => v.voice_id === voiceId);
    if (!voice?.preview_url) return;
    if (previewAudioRef.current) { previewAudioRef.current.pause(); previewAudioRef.current = null; }
    if (playingPreview === voiceId) { setPlayingPreview(null); return; }
    const audio = new Audio(voice.preview_url);
    audio.onended = () => setPlayingPreview(null);
    audio.play();
    previewAudioRef.current = audio;
    setPlayingPreview(voiceId);
  };

  // ── Production (Video) ──
  const handleGeneratePrompt = async () => {
    setGenPromptLoading(true);
    try {
      await updateCurrentScene({ ...sceneForm, ...actionsForm, audio_direction: audioDirection, dialogue_lines: dialogueLines });
      const sid = currentScene.id;
      await api.post(`/projects/${projectId}/scenes/${sid}/generate-prompt`, {}, { timeout: 20000 });
      let attempts = 0;
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        const st = await api.get(`/projects/${projectId}/scenes/${sid}/prompt-gen-status`, { params: { _t: Date.now() } });
        if (st.data.status === 'complete') {
          const vp = st.data.scene?.video_prompt || '';
          setVideoPrompt(vp); setCurrentScene(prev => ({ ...prev, video_prompt: vp }));
          toast.success('Prompt generated!');
          return;
        }
        if (st.data.status === 'failed') {
          toast.error(st.data.error || 'The model returned an empty prompt. Please try again.');
          return;
        }
      }
      toast.error('Prompt generation timed out. Please try again.');
    } catch (err) { handleGenError(err, 'Failed'); }
    finally { setGenPromptLoading(false); }
  };
  const handleSavePrompt = async () => { await updateCurrentScene({ video_prompt: videoPrompt }); setEditingPrompt(false); toast.success('Saved!'); };
  const handleGenerateVideo = async () => {
    const sceneId = currentScene?.id;
    if (!sceneId) return;
    const unassigned = (dialogueLines || []).filter(l => l.text?.trim() && !l.character_id).length;
    if (unassigned > 0) toast.warning(`${unassigned} dialogue line${unassigned > 1 ? 's have' : ' has'} no character assigned — assign one so it's spoken by the right character.`);
    setGenVideoLoading(true); setVideoStatus(null);
    setGenStartTime(Date.now()); setElapsedSec(0);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    const t0 = Date.now();
    elapsedTimerRef.current = setInterval(() => setElapsedSec(Math.floor((Date.now() - t0) / 1000)), 1000);
    try {
      await updateCurrentScene({ video_prompt: videoPrompt });
      await api.post(`/projects/${projectId}/scenes/${sceneId}/generate-video?provider=${videoProvider}&duration=${videoDuration}${(videoProvider === 'seedance' || videoProvider === 'kling') ? `&resolution=${videoResolution}` : ''}`);
      refreshJobs();
      toast.success('Generation started — you can move on; track it in the Activity panel.');
      pollVideoStatus(sceneId);
    } catch (err) {
      handleGenError(err, 'Failed'); setGenVideoLoading(false);
      if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
      setGenStartTime(null);
    }
  };
  const stopElapsedTimer = () => {
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    setGenStartTime(null);
  };
  const pollVideoStatus = (sceneId) => {
    if (videoPollingRef.current) { clearTimeout(videoPollingRef.current); videoPollingRef.current = null; }
    const targetId = sceneId || pollingSceneIdRef.current;
    if (!targetId) { console.error('pollVideoStatus: no scene ID available'); return; }
    pollingSceneIdRef.current = targetId;
    const startTime = Date.now();
    const poll = async () => {
      if (Date.now() - startTime > 35 * 60 * 1000) { pollingSceneIdRef.current = null; videoPollingRef.current = null; setGenVideoLoading(false); stopElapsedTimer(); toast.error('Still no result after 35 minutes — the job was stopped. Check the Activity panel or retry.'); return; }
      try {
        const res = await api.get(`/projects/${projectId}/scenes/${targetId}/video-status`, { params: { _t: Date.now() } });
        setVideoStatus(res.data);
        if (res.data.status === 'complete') {
          pollingSceneIdRef.current = null; videoPollingRef.current = null; setGenVideoLoading(false); stopElapsedTimer();
          const scenesRes = await api.get(`/projects/${projectId}/scenes`);
          const updated = scenesRes.data.find(s => s.id === targetId);
          if (updated) { setCurrentScene(updated); setScenes(scenesRes.data); }
          toast.success('Video ready!');
          return;
        } else if (res.data.status === 'cancelled') {
          pollingSceneIdRef.current = null; videoPollingRef.current = null; setGenVideoLoading(false); stopElapsedTimer();
          return;
        } else if (res.data.status === 'failed') {
          pollingSceneIdRef.current = null; videoPollingRef.current = null; setGenVideoLoading(false); stopElapsedTimer();
          toast.error(res.data.error || 'Failed');
          return;
        }
      } catch (err) {
        console.error('Video poll error:', err);
      }
      videoPollingRef.current = setTimeout(poll, 5000);
    };
    videoPollingRef.current = setTimeout(poll, 3000);
  };
  const handleLockAndNext = async () => {
    setVideoStatus(null); setVideoPrompt('');
    setGenVideoLoading(false); stopElapsedTimer();
    if (videoPollingRef.current) { clearTimeout(videoPollingRef.current); videoPollingRef.current = null; }
    pollingSceneIdRef.current = null;
    handleIdentifyNextScene();
  };

  const durationOptions = videoProvider === 'kling' ? Array.from({ length: 13 }, (_, i) => i + 3) : videoProvider === 'seedance' ? Array.from({ length: 27 }, (_, i) => i + 4) : [5,6,7,8];
  const suggestDuration = () => {
    if (!currentScene) return null;
    const lines = (dialogueLines || []).filter(l => l.text?.trim());
    const dialogueSeconds = lines.reduce((sum, l) => sum + (l.text?.length || 0), 0) / 12;
    const actionComplexity = ((actionsForm.start_action || '').length + (actionsForm.end_action || '').length) / 200;
    const cam = sceneForm.camera_movement || '';
    const camBonus = ['Tracking Shot','Orbit','Crane Up','Crane Down','Dolly Forward','Dolly Back'].includes(cam) ? 1.5 : 0;
    const raw = 3 + dialogueSeconds + actionComplexity + camBonus;
    const maxDur = videoProvider === 'veo' ? 8 : videoProvider === 'kling' ? 15 : 30;
    const minDur = videoProvider === 'kling' ? 3 : videoProvider === 'seedance' ? 4 : 5;
    return { seconds: Math.round(Math.min(maxDur, Math.max(minDur, raw))) };
  };

  // ── Wizard Navigation Helpers ──
  const canGoForward = (step) => {
    if (step === 0) {
      if (sceneCharacters.length === 0) return true; // No characters = skip
      return sceneCharacters.every(c => charExtras[c.id] || c.image_storage_path);
    }
    if (step === 1) return !!currentScene?.location_ref_path || !!sceneForm.scene_prompt;
    if (step === 2) return simple || !!(currentScene?.first_frame_path && currentScene?.last_frame_path);
    if (step === 3) return true; // Audio is optional
    return true;
  };

  if (loading) return <div className="min-h-screen bg-[#060608] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#FF4500] animate-spin" /></div>;

  if (!project) return (
    <div className="min-h-screen bg-[#060608] flex items-center justify-center">
      <div className="text-center">
        <Film className="w-12 h-12 text-[#55555C] mx-auto mb-4" />
        <p className="text-[#8A8A93] mb-4">Unable to load project</p>
        <button onClick={() => navigate('/dashboard')} className="ff-btn-primary">Back to Dashboard</button>
      </div>
    </div>
  );

  const needsFirstScene = scenes.length === 0 && !identifyingScene && !currentScene;

  return (
    <div className="min-h-screen bg-[#060608] pb-40 md:pb-0" data-testid="production-mode">
      <ImageLightbox src={lightbox?.url} title={lightbox?.title} downloadName={lightbox?.name} onClose={() => setLightbox(null)} />
      {mediaPicker && (
        <MediaPicker open={!!mediaPicker} onClose={() => setMediaPicker(null)} projectId={projectId}
          mediaType={mediaPicker.mediaType} title={mediaPicker.title} onSelect={handleUseMedia}
          frameTabs={mediaPicker.slot === 'first_frame' || mediaPicker.slot === 'last_frame'} />
      )}
      {/* ── Sticky Header ── */}
      <div className="sticky top-0 z-40 bg-[#060608]/95 backdrop-blur-xl border-b border-white/[0.04]">
        <div className="max-w-4xl mx-auto px-3 md:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
              <button data-testid="production-back" onClick={() => navigate(singleMode ? `/project/${projectId}?tab=scenes` : `/project/${projectId}`)} className="text-[#8A8A93] hover:text-white flex-shrink-0"><ArrowLeft className="w-4 h-4" /></button>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-xs md:text-sm font-bold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{project?.name}</h1>
                  <span className="text-[8px] md:text-[9px] px-1.5 md:px-2 py-0.5 rounded-full bg-[#FF4500]/10 text-[#FF4500] border border-[#FF4500]/20 font-semibold tracking-wider uppercase flex-shrink-0">{singleMode ? 'Scene' : 'Production'}</span>
                </div>
                <div className="[&_nav]:mb-0 hidden md:block">
                  <Breadcrumbs items={[
                    { label: project?.name || 'Project', to: `/project/${projectId}${singleMode ? '?tab=scenes' : ''}` },
                    { label: currentScene ? `Scene ${scenes.findIndex(x => x.id === currentScene.id) + 1}${currentScene.title ? ` · ${currentScene.title}` : ''}` : 'Scene' },
                    { label: WIZARD_STEPS[wizardStep]?.label || '' },
                  ]} />
                </div>
                <p className="text-[10px] text-[#55555C] md:hidden">{singleMode ? (currentScene?.title || 'Scene') : `${completedScenes} of ${scenes.length} scenes completed`}</p>
              </div>
            </div>

            {/* Scene dots - scrollable on mobile */}
            {!singleMode && <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide ml-2 flex-shrink-0">
              {scenes.map((s, i) => (
                <button key={s.id} onClick={() => { if (s.id !== currentScene?.id) loadSceneIntoState(s, characters); }}
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-bold transition-all ${
                    s.id === currentScene?.id ? 'bg-[#FF4500] text-white ring-2 ring-[#FF4500]/30'
                    : s.video_path ? 'bg-[#10B981]/20 text-[#10B981] hover:bg-[#10B981]/30 cursor-pointer'
                    : 'bg-white/[0.04] text-[#55555C] hover:bg-white/[0.08] cursor-pointer'
                  }`}>
                  {s.video_path ? <Check className="w-3 h-3" /> : i + 1}
                </button>
              ))}
              {!isFinalScene && <div className="w-6 h-6 rounded-full bg-white/[0.02] border border-dashed border-white/[0.08] flex items-center justify-center text-[8px] text-[#55555C]">+</div>}
            </div>}

            <div className="flex items-center gap-2">
              {currentScene && (
                <button data-testid="scene-languages-btn" onClick={() => setShowLanguages(true)} title="Translated versions of this scene"
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-[#00F0FF]/30 text-[#00F0FF] bg-[#00F0FF]/5 hover:bg-[#00F0FF]/15 transition-colors">
                  <Languages className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Languages</span>
                  {Object.keys(currentScene.language_versions || {}).length > 0 && <span className="px-1.5 rounded-full bg-[#00F0FF]/20">{Object.keys(currentScene.language_versions).length}</span>}
                </button>
              )}
              <span className="text-xs font-semibold text-white">{currentScene ? `Scene ${sceneNumber}` : '—'}</span>
              {genVideoLoading && (
                <button onClick={handleFlushJobs} disabled={flushingJobs} data-testid="reset-stuck-header"
                  title="Reset stuck generation jobs"
                  className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 flex items-center gap-1">
                  {flushingJobs ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RotateCcw className="w-2.5 h-2.5" />} Reset
                </button>
              )}
            </div>
          </div>

          {/* Wizard step breadcrumbs - scrollable on mobile */}
          {currentScene && !identifyingScene && (
            <div className="hidden md:flex items-center gap-1 mt-2 overflow-x-auto scrollbar-hide pb-1">
              {visibleSteps.map((s) => {
                const i = WIZARD_STEPS.findIndex(w => w.key === s.key);
                const Icon = s.icon;
                const isActive = i === wizardStep;
                const isDone = i < wizardStep;
                return (
                  <button key={s.key} onClick={() => setWizardStep(i)}
                    className={`flex items-center gap-1 md:gap-1.5 px-2 md:px-3 py-1.5 rounded-lg text-[10px] md:text-[11px] font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                      isActive ? 'bg-[#FF4500]/15 text-[#FF4500] border border-[#FF4500]/30'
                      : isDone ? 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20 cursor-pointer hover:bg-[#10B981]/20'
                      : 'text-[#8A8A93] border border-white/[0.06] cursor-pointer hover:text-white hover:border-white/20'
                    }`} data-testid={`wizard-step-${s.key}`}>
                    {isDone ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />} <span className="hidden sm:inline">{s.label}</span><span className="sm:hidden">{s.label.split(' ')[0]}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-3 md:px-6 py-4 md:py-8">
        {/* ── First Scene / Identifying ── */}
        {(needsFirstScene || identifyingScene) && (
          <div className="flex flex-col items-center justify-center py-20">
            {identifyingScene ? (
              <>
                <div className="relative mb-8">
                  <div className="w-24 h-24 rounded-full bg-[#FF4500]/10 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-[#FF4500]/20 flex items-center justify-center animate-pulse">
                      <Sparkles className="w-8 h-8 text-[#FF4500]" />
                    </div>
                  </div>
                </div>
                <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Identifying Scene {scenes.length + 1}...</h2>
                <p className="text-sm text-[#8A8A93]">Analyzing the next scene from your script</p>
              </>
            ) : (
              <>
                <Film className="w-16 h-16 text-[#FF4500] mb-6" />
                <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Ready to Start</h2>
                <p className="text-sm text-[#8A8A93] mb-6 text-center max-w-md">Your AI assistant will identify the first scene and guide you step by step.</p>
                <button data-testid="identify-first-scene" onClick={handleIdentifyNextScene} className="ff-btn-primary flex items-center gap-2 px-8 py-3 text-base">
                  <Sparkles className="w-5 h-5" /> Identify First Scene
                </button>
              </>
            )}
          </div>
        )}

        {/* ── Main Wizard ── */}
        {currentScene && !identifyingScene && (
          <>
            {/* Image Provider + Aspect Ratio Toggle */}
            {wizardStep < 3 && (
              <div className="flex flex-wrap items-center justify-end gap-2 md:gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#55555C] tracking-wide uppercase">Format:</span>
                  <div className="flex gap-1 bg-[#0a0a0e] rounded-lg p-0.5 border border-white/[0.06]">
                    {[{ id: '16:9', box: 'w-3 h-2' }, { id: '9:16', box: 'w-2 h-3' }, { id: '1:1', box: 'w-2.5 h-2.5' }].map(f => (
                      <button key={f.id} data-testid={`format-${f.id.replace(':', 'x')}`} onClick={() => updateProjectSetting({ aspect_ratio: f.id })}
                        className={`px-2 py-1 rounded-md text-[10px] font-medium transition-all flex items-center gap-1 ${(project?.aspect_ratio || '16:9') === f.id ? 'bg-[#00F0FF]/15 text-[#00F0FF]' : 'text-[#8A8A93]'}`}>
                        <span className={`${f.box} rounded-[1px] border border-current inline-block`} /> {f.id}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[#55555C] tracking-wide uppercase">Image AI:</span>
                  <div className="flex gap-1 bg-[#0a0a0e] rounded-lg p-0.5 border border-white/[0.06]">
                    <button onClick={() => setImageProvider('gemini')} className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${imageProvider === 'gemini' ? 'bg-[#FF4500]/15 text-[#FF4500]' : 'text-[#8A8A93]'}`}>Gemini</button>
                    <button onClick={() => setImageProvider('openai')} className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-all ${imageProvider === 'openai' ? 'bg-[#10B981]/15 text-[#10B981]' : 'text-[#8A8A93]'}`}>GPT Image</button>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ STEP 1: CHARACTERS ═══ */}
            {wizardStep === 0 && (
              <div className="space-y-4" data-testid="step-characters">
                {sceneCharacters.length === 0 ? (
                  <div className="ff-card p-8 text-center">
                    <Users className="w-10 h-10 text-[#55555C] mx-auto mb-3 opacity-40" />
                    <p className="text-sm text-[#8A8A93] mb-1">No characters identified for this scene</p>
                    <p className="text-xs text-[#55555C] mb-4">Add characters from this project or any other project in your workspace.</p>
                    <button data-testid="add-character-btn" onClick={() => setShowAddChar(true)} className="ff-btn-primary inline-flex items-center gap-2 text-xs"><Plus className="w-3.5 h-3.5" /> Add character</button>
                  </div>
                ) : (
                  <>
                {/* Character counter */}
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Character {charIndex + 1} of {sceneCharacters.length}
                  </h3>
                  <div className="flex gap-1 items-center">
                    <button data-testid="add-character-btn" onClick={() => setShowAddChar(true)} title="Add a character to this scene"
                      className="w-8 h-8 rounded-lg text-[#8A8A93] hover:text-white border border-dashed border-white/15 hover:border-[#FF4500]/50 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                    {sceneCharacters.map((c, i) => (
                      <button key={c.id} onClick={() => setCharIndex(i)}
                        className={`w-8 h-8 rounded-lg text-[10px] font-bold flex items-center justify-center transition-all ${
                          i === charIndex ? 'bg-[#FF4500] text-white' : charExtras[c.id] ? 'bg-[#55555C]/20 text-[#55555C]' : c.image_storage_path ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-white/[0.04] text-[#8A8A93]'
                        }`}>
                        {c.name?.charAt(0)}
                      </button>
                    ))}
                  </div>
                </div>

                {currentChar && (
                  <div className="ff-card p-6" data-testid={`char-card-${currentChar.id}`}>
                    {/* Extra toggle */}
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-base font-bold text-white">{currentChar.name}</h4>
                      <div className="flex items-center gap-2">
                      <button data-testid={`link-existing-char-${currentChar.id}`} onClick={() => setLinkChar(currentChar)} title="This is an existing character from another project"
                        className="text-[10px] px-3 py-1 rounded-full border flex items-center gap-1.5 bg-[#00F0FF]/5 text-[#00F0FF] border-[#00F0FF]/25 hover:bg-[#00F0FF]/15 transition-all">
                        <Users className="w-3 h-3" /> Use existing character
                      </button>
                      <button data-testid={`extra-toggle-${currentChar.id}`}
                        onClick={() => setCharExtras(prev => ({ ...prev, [currentChar.id]: !prev[currentChar.id] }))}
                        className={`text-[10px] px-3 py-1 rounded-full border flex items-center gap-1.5 transition-all ${
                          charExtras[currentChar.id] ? 'bg-[#55555C]/10 text-[#55555C] border-[#55555C]/20' : 'bg-white/[0.02] text-[#8A8A93] border-white/[0.06] hover:border-white/20'
                        }`}>
                        <UserX className="w-3 h-3" /> {charExtras[currentChar.id] ? 'Extra (no consistency needed)' : 'Mark as Extra'}
                      </button>
                      </div>
                    </div>

                    {charExtras[currentChar.id] ? (
                      <div className="text-center py-8 text-[#55555C]">
                        <UserX className="w-10 h-10 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">This character is an extra — the AI will generate them freely without consistency requirements.</p>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-4 sm:gap-5">
                        {/* Portrait */}
                        <div className="w-full sm:w-32 flex-shrink-0">
                          <div className="w-full sm:w-32 h-40 rounded-xl overflow-hidden bg-[#0a0a0e] border border-white/[0.06] mb-2 relative group">
                            {currentChar.image_storage_path ? (
                              <img src={getImageUrl(currentChar.image_storage_path)} alt={currentChar.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-[#55555C]"><Users className="w-10 h-10" /></div>
                            )}
                            {charLocked[currentChar.id] && currentChar.image_storage_path && (
                              <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#10B981]/90 flex items-center justify-center">
                                <Lock className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>

                          {/* Lock/Unlock + Generate/Upload */}
                          {charLocked[currentChar.id] && currentChar.image_storage_path ? (
                            <button data-testid={`unlock-char-${currentChar.id}`}
                              onClick={() => setShowUnlockWarning(currentChar.id)}
                              className="w-full text-[9px] py-1.5 rounded bg-white/[0.04] text-[#8A8A93] hover:text-white flex items-center justify-center gap-1 transition-colors">
                              <Unlock className="w-2.5 h-2.5" /> Unlock to Regenerate
                            </button>
                          ) : (
                            <div className="flex gap-1">
                              <input ref={el => charUploadRefs.current[currentChar.id] = el} type="file" accept="image/*" className="hidden"
                                onChange={e => { if (e.target.files?.[0]) handleUploadCharImage(currentChar.id, e.target.files[0]); }} />
                              <button onClick={() => charUploadRefs.current[currentChar.id]?.click()} disabled={!!genCharImgId}
                                className="flex-1 text-[9px] py-1.5 rounded bg-white/[0.04] text-[#8A8A93] hover:text-white disabled:opacity-40 flex items-center justify-center gap-0.5">
                                <Upload className="w-2.5 h-2.5" /> Upload
                              </button>
                              <button onClick={() => handleGenCharImage(currentChar.id)} disabled={!!genCharImgId}
                                className="flex-1 text-[9px] py-1.5 rounded bg-[#FF4500]/10 text-[#FF4500] hover:bg-[#FF4500]/20 disabled:opacity-40 flex items-center justify-center gap-0.5">
                                {genCharImgId === currentChar.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />}
                                Generate
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Description + Outfit */}
                        <div className="flex-1 min-w-0 space-y-3">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-[10px] text-[#55555C]">Character Description</label>
                              <button onClick={() => handleSaveCharDescription(currentChar.id, currentChar.description)}
                                data-testid={`save-char-desc-prod-${currentChar.id}`}
                                className="text-[9px] text-[#10B981] hover:text-[#34D399] flex items-center gap-1">
                                <Save className="w-2.5 h-2.5" /> Save
                              </button>
                            </div>
                            <textarea value={currentChar.description || ''}
                              onChange={e => setCharacters(prev => prev.map(c => c.id === currentChar.id ? { ...c, description: e.target.value } : c))}
                              onBlur={e => handleUpdateCharDescription(currentChar.id, e.target.value)}
                              className="ff-input w-full h-32 resize-y text-xs leading-relaxed" placeholder="Physical appearance, personality, wardrobe..." />
                          </div>

                          {/* Outfit */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <label className="text-[10px] text-[#A78BFA] flex items-center gap-1"><Shirt className="w-3 h-3" /> Outfit for this Scene</label>
                              <div className="flex gap-2">
                                {projectRefs.character_refs.filter(r => r.character_id === currentChar.id && r.scene_id !== currentScene.id).length > 0 && (
                                  <button onClick={() => setShowRefPicker(showRefPicker === `char-${currentChar.id}` ? null : `char-${currentChar.id}`)}
                                    className="text-[9px] text-[#A78BFA] hover:text-[#C4B5FD] flex items-center gap-0.5"><Grid3x3 className="w-2.5 h-2.5" /> Reuse</button>
                                )}
                                <button onClick={() => handleSuggestOutfit(currentChar.id)} disabled={suggestingOutfit === currentChar.id}
                                  className="text-[9px] text-[#F59E0B] hover:text-[#FBBF24] flex items-center gap-0.5 disabled:opacity-40">
                                  {suggestingOutfit === currentChar.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Wand2 className="w-2.5 h-2.5" />} AI Suggest
                                </button>
                              </div>
                            </div>

                            {showRefPicker === `char-${currentChar.id}` && (
                              <div className="mb-2 p-2 rounded-lg border border-[#A78BFA]/20 bg-[#12121a]">
                                <p className="text-[9px] uppercase text-[#55555C] mb-1.5">Previous looks</p>
                                <div className="grid grid-cols-4 gap-1.5">
                                  {projectRefs.character_refs.filter(r => r.character_id === currentChar.id && r.scene_id !== currentScene.id).map((ref, i) => (
                                    <button key={i} onClick={() => handleSelectCharRef(currentChar.id, currentChar.name, ref.path, ref.outfit_description)}
                                      className="rounded-lg overflow-hidden border border-white/[0.06] hover:border-[#A78BFA]/40 transition-all">
                                      <div className="aspect-[3/4] bg-[#0a0a0e]"><img src={getImageUrl(ref.path)} alt="" className="w-full h-full object-cover" /></div>
                                      <p className="text-[8px] text-[#55555C] p-1 truncate">{ref.outfit_description || ref.scene_title}</p>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {(() => {
                              const sceneRef = (currentScene.character_scene_refs || []).find(r => r.character_id === currentChar.id);
                              return sceneRef?.ref_image_path && (
                                <div className="mb-2 w-20 h-24 rounded-lg overflow-hidden border border-[#A78BFA]/20">
                                  <img src={getImageUrl(sceneRef.ref_image_path)} alt="" className="w-full h-full object-cover" />
                                </div>
                              );
                            })()}

                            <div className="flex gap-2">
                              <input value={charOutfits[currentChar.id] || ''} onChange={e => setCharOutfits(prev => ({ ...prev, [currentChar.id]: e.target.value }))}
                                className="ff-input flex-1 text-[11px] py-1.5" placeholder="Outfit description..." />
                              <button onClick={() => handleGenerateCharRef(currentChar.id)} disabled={genCharRefLoading === currentChar.id}
                                className="text-[10px] px-3 py-1.5 rounded bg-[#A78BFA]/15 text-[#A78BFA] hover:bg-[#A78BFA]/25 border border-[#A78BFA]/30 disabled:opacity-40 flex items-center gap-1 whitespace-nowrap">
                                {genCharRefLoading === currentChar.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Generate
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                  </>
                )}

                {/* Char navigation + Continue */}
                <div className="flex justify-between">
                  <div className="flex gap-2">
                    {charIndex > 0 && (
                      <button onClick={() => setCharIndex(i => i - 1)} className="ff-btn-secondary flex items-center gap-1 text-sm"><ChevronLeft className="w-4 h-4" /> Previous</button>
                    )}
                  </div>
                  {charIndex < sceneCharacters.length - 1 ? (
                    <button onClick={() => setCharIndex(i => i + 1)} className="ff-btn-primary flex items-center gap-2 text-sm">
                      Next Character <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button data-testid="chars-continue" onClick={() => setWizardStep(1)} disabled={!canGoForward(0)}
                      className="ff-btn-primary flex items-center gap-2 disabled:opacity-40">
                      Continue to Scene <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ═══ STEP 2: SCENE & LOCATION ═══ */}
            {wizardStep === 1 && (
              <div className="space-y-4" data-testid="step-scene">
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Scene & Location</h3>
                <div className="ff-card p-5 space-y-4">
                  <div>
                    <label className="text-[10px] text-[#55555C] block mb-1">Scene Description</label>
                    <LockablePrompt
                      key={currentScene?.id}
                      testId="scene-prompt"
                      value={sceneForm.scene_prompt}
                      onSave={async (v) => { setSceneForm(f => ({ ...f, scene_prompt: v })); await updateCurrentScene({ scene_prompt: v }); }}
                      onEditingChange={setPromptEditing('scene')}
                      placeholder="Overall scene description..."
                      heightClass="h-24"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="text-[10px] text-[#55555C] block mb-1">Setting</label><input value={sceneForm.setting} onChange={e => setSceneForm(f => ({ ...f, setting: e.target.value }))} className="ff-input w-full text-xs" /></div>
                    <div><label className="text-[10px] text-[#55555C] block mb-1">Mood</label>
                      <select value={sceneForm.mood} onChange={e => setSceneForm(f => ({ ...f, mood: e.target.value }))} className="ff-input w-full text-xs">
                        {['Tense','Peaceful','Mysterious','Epic','Intimate','Melancholic','Joyful','Horror','Action','Romantic','Surreal'].map(m => <option key={m} value={m}>{m}</option>)}
                      </select></div>
                    <div><label className="text-[10px] text-[#55555C] block mb-1">Time of Day</label>
                      <select value={sceneForm.time_of_day} onChange={e => setSceneForm(f => ({ ...f, time_of_day: e.target.value }))} className="ff-input w-full text-xs">
                        {['Dawn','Morning','Midday','Afternoon','Golden Hour','Dusk','Night','Neon-lit','Artificial Lighting'].map(t => <option key={t} value={t}>{t}</option>)}
                      </select></div>
                    <div><label className="text-[10px] text-[#55555C] block mb-1">Camera</label>
                      <select value={sceneForm.camera_movement} onChange={e => setSceneForm(f => ({ ...f, camera_movement: e.target.value }))} className="ff-input w-full text-xs">
                        {['Static','Slow Pan Left','Slow Pan Right','Slow Zoom In','Slow Zoom Out','Dolly Forward','Dolly Back','Tracking Shot','Crane Up','Crane Down','Orbit','Handheld'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select></div>
                  </div>
                </div>

                {/* Locations (opening + optional closing) */}
                <SceneLocations projectId={projectId} scene={currentScene} imageProvider={imageProvider}
                  onSceneUpdated={(sc) => { setCurrentScene(sc); setScenes(prev => prev.map(x => x.id === sc.id ? sc : x)); }} />

                <div className="flex justify-between">
                  <button onClick={() => setWizardStep(0)} className="ff-btn-secondary flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Characters</button>
                  <button data-testid="scene-continue" onClick={async () => { setSavingScene(true); await updateCurrentScene(sceneForm); setSavingScene(false); setWizardStep(2); }}
                    disabled={savingScene} className="ff-btn-primary flex items-center gap-2">
                    {savingScene ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />} Continue to Frames
                  </button>
                </div>
              </div>
            )}

            {/* ═══ STEP 3: FRAMES ═══ */}
            {wizardStep === 2 && (
              <div className="space-y-4" data-testid="step-frames">
                {!simple && <ReferenceStrip scene={currentScene} characters={characters} locations={sceneLocations} />}
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{simple ? 'Opening & Closing Frames (optional)' : 'Opening & Closing Frames'}</h3>
                <p className="text-xs text-[#8A8A93]">{simple ? 'Generate from a short description, upload your own image, or pick existing media. Leave both empty to render straight from the video prompt in the final step.' : 'Use @character names in descriptions for consistency with your character references.'}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[{ type: 'first', label: 'Opening Frame', color: '#FF4500', field: 'start_action', path: 'first_frame_path', uploadRef: startFrameUploadRef },
                    { type: 'last', label: 'Closing Frame', color: '#00F0FF', field: 'end_action', path: 'last_frame_path', uploadRef: endFrameUploadRef }
                  ].map(frame => (
                    <div key={frame.type} className="ff-card p-4">
                      <span className="text-[10px] tracking-[0.15em] uppercase font-semibold block mb-2" style={{ color: frame.color }}>{frame.label}</span>
                      <div className="mb-3">
                        <LockablePrompt
                          key={`${frame.type}-${currentScene?.id}`}
                          testId={`frame-${frame.type}-prompt`}
                          value={actionsForm[frame.field]}
                          onSave={async (v) => { setActionsForm(f => ({ ...f, [frame.field]: v })); await updateCurrentScene({ [frame.field]: v }); }}
                          onEditingChange={setPromptEditing(`frame-${frame.type}`)}
                          mention
                          characters={sceneCharacters}
                          placeholder={simple ? `What the ${frame.type === 'first' ? 'opening' : 'closing'} image shows (optional — blank uses the video prompt)` : `What happens at the ${frame.type === 'first' ? 'start' : 'end'}... Use @name to tag characters`}
                          heightClass="h-20"
                        />
                      </div>
                      <div className={`${project?.aspect_ratio === '9:16' ? 'aspect-[9/16] max-h-[300px]' : project?.aspect_ratio === '1:1' ? 'aspect-square max-h-[300px]' : 'aspect-video'} bg-[#0a0a0e] rounded-lg overflow-hidden border border-white/[0.06] mb-2`}>
                        {currentScene[frame.path] ? (
                          <button type="button" onClick={() => setLightbox({ url: getImageUrl(currentScene[frame.path]), title: frame.label, name: `${currentScene.title || 'scene'}-${frame.type}.jpg` })}
                            className="group relative w-full h-full block cursor-zoom-in" data-testid={`enlarge-frame-${frame.type}`}>
                            <img src={getImageUrl(currentScene[frame.path])} alt={frame.label} className="w-full h-full object-cover" />
                            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <span className="flex items-center gap-1.5 text-xs font-medium text-white bg-black/50 px-2.5 py-1.5 rounded-lg"><Maximize2 className="w-3.5 h-3.5" /> Enlarge</span>
                            </span>
                          </button>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#55555C]"><ImageIcon className="w-6 h-6" /></div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => { handleGenerateFrame(frame.type); }} disabled={!!genFrameLoading || editingPrompts[`frame-${frame.type}`]}
                          data-testid={`gen-frame-${frame.type}`}
                          title={editingPrompts[`frame-${frame.type}`] ? 'Save the prompt before generating' : ''}
                          className="flex-1 text-[10px] py-1.5 rounded disabled:opacity-40 flex items-center justify-center gap-1"
                          style={{ background: `${frame.color}15`, color: frame.color, border: `1px solid ${frame.color}30` }}>
                          {genFrameLoading === frame.type ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          {currentScene[frame.path] ? 'Regen' : 'Generate'}
                        </button>
                        <input ref={frame.uploadRef} type="file" accept="image/*" onChange={e => handleUploadFrame(e, frame.type)} className="hidden" />
                        <button onClick={() => frame.uploadRef.current?.click()} className="text-[10px] py-1.5 px-2 rounded bg-white/[0.04] text-[#8A8A93] hover:text-white flex items-center gap-1"><Upload className="w-3 h-3" /></button>
                        {currentScene[frame.path] && (
                          <button onClick={() => handleDownloadFrame(currentScene[frame.path], `${currentScene.title || 'scene'}-${frame.type}.jpg`)}
                            className="text-[10px] py-1.5 px-2 rounded bg-white/[0.04] text-[#00F0FF] hover:text-white flex items-center gap-1"><Download className="w-3 h-3" /></button>
                        )}
                      </div>
                      <p data-testid={`cost-frame-${frame.type}`} className="text-[9px] text-[#55555C] mt-1">Est. cost ~{formatCost(estimateFrameCost(imageProvider, frame.type, rates))} · {imageProvider === 'openai' ? 'GPT Image' : 'Gemini'}</p>
                      <div className="mt-2"><FrameEditor projectId={projectId} sceneId={currentScene.id} which={frame.type} scene={currentScene} imageProvider={imageProvider}
                        onUpdated={(s) => { setCurrentScene(s); setScenes(prev => prev.map(x => x.id === s.id ? s : x)); }} refreshJobs={refreshJobs} align="left" /></div>
                      {frame.type === 'last' && currentScene.first_frame_path && (
                        <button data-testid="toggle-opening-ref" onClick={() => setUseOpeningRef(v => !v)}
                          className={`w-full mt-2 text-[10px] py-2 rounded-lg flex items-center justify-center gap-1.5 transition-colors border ${
                            useOpeningRef
                              ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30'
                              : 'bg-white/[0.02] text-[#8A8A93] border-white/[0.06] hover:border-white/20'
                          }`}>
                          <Eye className="w-3 h-3" /> {useOpeningRef ? 'Matching opening frame (on)' : 'Match opening frame'}
                        </button>
                      )}
                      {frame.type === 'first' && prevClosingFrame && currentScene.first_frame_path === prevClosingFrame && (
                        <p className="text-[9px] text-[#E879F9]/60 mt-1.5 flex items-center gap-1"><Link2 className="w-2.5 h-2.5" /> Linked from previous scene</p>
                      )}
                      {frame.type === 'first' && prevClosingFrame && currentScene.first_frame_path !== prevClosingFrame && (
                        <button data-testid="use-prev-closing-frame" onClick={handleUsePrevClosingFrame}
                          className="w-full mt-2 text-[10px] py-2 rounded-lg bg-[#00F0FF]/10 text-[#00F0FF] hover:bg-[#00F0FF]/20 border border-[#00F0FF]/20 flex items-center justify-center gap-1.5 transition-colors">
                          <Link2 className="w-3 h-3" /> Use previous scene's closing frame
                        </button>
                      )}
                      <button data-testid={`use-existing-${frame.type}`} onClick={() => setMediaPicker({ slot: frame.type === 'first' ? 'first_frame' : 'last_frame', mediaType: 'image', title: `Use Existing Image — ${frame.label}` })}
                        className="w-full mt-2 text-[10px] py-2 rounded-lg bg-[#E879F9]/10 text-[#E879F9] hover:bg-[#E879F9]/20 border border-[#E879F9]/20 flex items-center justify-center gap-1.5 transition-colors">
                        <Link2 className="w-3 h-3" /> {currentScene[frame.path] ? 'Swap for existing media' : 'Use existing media'}
                      </button>
                      <FrameRefPicker which={frame.type} label={frame.label} uploading={refUploading === frame.type}
                        refPath={currentScene[frame.type === 'first' ? 'first_frame_ref_path' : 'last_frame_ref_path']}
                        onUpload={handleUploadFrameRef} onRemove={handleRemoveFrameRef}
                        onPick={(w) => setMediaPicker({ slot: w === 'first' ? 'first_frame_ref' : 'last_frame_ref', mediaType: 'image', title: `Reference for ${frame.label}` })} />
                    </div>
                  ))}
                </div>
                {frameError && (
                  <div data-testid="frame-error-banner" className="flex items-start gap-3 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-xs text-red-400 font-medium">Frame generation failed</p>
                      <p className="text-[11px] text-red-300/80 mt-0.5 break-words">{frameError}</p>
                      <p className="text-[11px] text-[#8A8A93] mt-1">{failureHint(frameError)}</p>
                    </div>
                  </div>
                )}
                <div className="flex justify-between">
                  {simple ? <span /> : <button onClick={() => setWizardStep(1)} className="ff-btn-secondary flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Scene</button>}
                  <button data-testid="frames-continue" onClick={async () => { await handleSaveActions(); setWizardStep(3); }}
                    className="ff-btn-primary flex items-center gap-2">Continue to Audio <ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}

            {/* ═══ STEP 4: AUDIO ═══ */}
            {wizardStep === 3 && (
              <div className="space-y-4" data-testid="step-audio">
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{simple ? 'Audio (optional)' : 'Audio & Dialogue'}</h3>
                <div className="ff-card p-5">
                  <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#10B981] mb-3 flex items-center gap-1.5"><Volume2 className="w-3.5 h-3.5" /> Audio Direction</p>
                  <LockablePrompt
                    key={currentScene?.id}
                    testId="audio-direction"
                    value={audioDirection}
                    onSave={async (v) => { setAudioDirection(v); await updateCurrentScene({ audio_direction: v }); }}
                    onEditingChange={setPromptEditing('audio')}
                    placeholder="Sound effects, ambient audio..."
                    heightClass="h-20"
                  />
                </div>
                {simple ? (
                  <div className="ff-card p-5" data-testid="simple-audio-track-card">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#A78BFA] flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" /> Music / voice-over track</p>
                      <span className="text-[9px] text-[#55555C]">MP3 · WAV · M4A</span>
                    </div>
                    <p className="text-[11px] text-[#8A8A93] mb-3">Optional. Seedance uses it natively as the soundtrack; for Kling and Veo it's mixed under the finished clip.</p>
                    <input ref={refAudioUploadRef} type="file" accept="audio/*" onChange={e => handleUploadReference(e, 'audio')} className="hidden" />
                    {currentScene.reference_audio_path ? (
                      <div className="flex items-center gap-2">
                        <audio controls src={getImageUrl(currentScene.reference_audio_path)} className="h-8 flex-1 min-w-0" data-testid="simple-audio-player" />
                        <button data-testid="replace-ref-audio" onClick={() => refAudioUploadRef.current?.click()} disabled={uploadingRef === 'audio'} className="text-[10px] px-2 py-1 rounded bg-white/[0.04] text-[#8A8A93] hover:text-white disabled:opacity-40">Replace</button>
                        <button data-testid="remove-ref-audio" onClick={() => handleDeleteReference('audio')} className="text-[10px] px-2 py-1 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20">Remove</button>
                      </div>
                    ) : (
                      <button data-testid="upload-ref-audio" onClick={() => refAudioUploadRef.current?.click()} disabled={uploadingRef === 'audio'}
                        className="w-full text-xs py-2 rounded-lg bg-[#A78BFA]/10 text-[#A78BFA] hover:bg-[#A78BFA]/20 disabled:opacity-40 flex items-center justify-center gap-1.5">
                        {uploadingRef === 'audio' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload a track
                      </button>
                    )}
                  </div>
                ) : (
                <div className="ff-card p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#A78BFA] flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" /> Dialogue</p>
                    <button onClick={addDialogueLine} className="text-[10px] text-[#A78BFA] flex items-center gap-1"><Plus className="w-3 h-3" /> Add Line</button>
                  </div>
                  {dialogueLines.length === 0 && <p className="text-xs text-[#55555C] p-2">No dialogue in this scene.</p>}
                  <div className="space-y-2">
                    {dialogueLines.map((line, idx) => (
                      <div key={line.id || idx} className="p-2.5 rounded bg-[#0a0a0e] border border-white/[0.04] space-y-2">
                        <div className="flex gap-2 items-start">
                          <select value={line.character_id} onChange={e => updateDialogueLine(idx, 'character_id', e.target.value)} className="ff-input text-[10px] py-1 px-1.5 w-28">
                            <option value="">Character...</option>
                            {sceneCharacters.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                          <textarea value={line.text} onChange={e => updateDialogueLine(idx, 'text', e.target.value)} className="ff-input flex-1 h-10 resize-none text-[11px]" placeholder="Dialogue..." />
                          <button onClick={() => setDialogueLines(prev => prev.filter((_, i) => i !== idx))} className="text-[#55555C] hover:text-red-400 p-1"><X className="w-3 h-3" /></button>
                        </div>
                        <div className="flex items-center gap-2">
                          <select value={line.elevenlabs_voice_id || ''} onChange={e => {
                            const v = elevenLabsVoices.find(v2 => v2.voice_id === e.target.value);
                            updateDialogueLine(idx, 'elevenlabs_voice_id', e.target.value);
                            updateDialogueLine(idx, 'elevenlabs_voice_name', v?.name || '');
                          }} onFocus={fetchElevenLabsVoices} className="ff-input text-[10px] py-1 px-1.5 flex-1">
                            <option value="">ElevenLabs Voice...</option>
                            {loadingVoices && <option disabled>Loading...</option>}
                            {line.elevenlabs_voice_id && !elevenLabsVoices.some(v => v.voice_id === line.elevenlabs_voice_id) && (
                              <option value={line.elevenlabs_voice_id}>{line.elevenlabs_voice_name || 'Assigned voice'}</option>
                            )}
                            {elevenLabsVoices.map(v => <option key={v.voice_id} value={v.voice_id}>{v.name} ({v.category})</option>)}
                          </select>
                          {line.elevenlabs_voice_id && (
                            <button onClick={() => playVoicePreview(line.elevenlabs_voice_id)}
                              className={`p-1.5 rounded-lg shrink-0 transition-all ${playingPreview === line.elevenlabs_voice_id ? 'bg-[#FF4500]/15 text-[#FF4500] border border-[#FF4500]/30' : 'bg-white/[0.04] text-[#8A8A93] hover:text-white border border-white/[0.06]'}`}>
                              {playingPreview === line.elevenlabs_voice_id ? <span className="w-3 h-3 block bg-[#FF4500] rounded-sm" /> : <Play className="w-3 h-3 fill-current" />}
                            </button>
                          )}
                          {line.tts_path && line.tts_provider === 'elevenlabs' && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20 whitespace-nowrap flex items-center gap-1"><Volume2 className="w-2.5 h-2.5" /> Ready</span>
                          )}
                          <NativeLinePreviewButton line={line} characters={characters} testId={`native-line-preview-${idx}`} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <NativeVoiceDialogueNotice dialogueLines={dialogueLines} characters={characters} preferred={currentScene?.native_voice_speakers}
                    onSavePreferred={(ids) => updateCurrentScene({ native_voice_speakers: ids })} />
                </div>
                )}
                {!simple && dialogueLines.some(l => l.text?.trim() && l.elevenlabs_voice_id) && (
                  <button data-testid="gen-tts-btn" onClick={handleGenerateElevenLabsTts} disabled={genTtsLoading}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25 border border-[#10B981]/30 disabled:opacity-40">
                    {genTtsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />} Generate with ElevenLabs
                  </button>
                )}
                <div className="flex justify-between">
                  <button onClick={() => setWizardStep(2)} className="ff-btn-secondary flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Frames</button>
                  <button data-testid="audio-continue" onClick={async () => { await handleSaveAudio(); setWizardStep(4); }} className="ff-btn-primary flex items-center gap-2">
                    Continue to Production <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* ═══ STEP 5: PRODUCTION ═══ */}
            {wizardStep === 4 && (
              <div className="space-y-4" data-testid="step-production">
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Production</h3>
                <PreflightChecklist scene={currentScene} dialogueLines={dialogueLines} characters={characters} provider={videoProvider} simple={simple}
                  onGoToStep={setWizardStep} onFixKey={() => navigate('/settings')} />

                {/* Frame preview */}
                <div className="grid grid-cols-2 gap-3">
                  {[{ path: 'first_frame_path', type: 'first', label: 'Opening Frame', color: '#FF4500' }, { path: 'last_frame_path', type: 'last', label: 'Closing Frame', color: '#00F0FF' }].map(f => (
                    <div key={f.path} className="ff-card overflow-hidden">
                      <div className={`${project?.aspect_ratio === '9:16' ? 'aspect-[9/16] max-h-[200px]' : project?.aspect_ratio === '1:1' ? 'aspect-square max-h-[200px]' : 'aspect-video'} bg-[#0a0a0e] relative group`}>
                        {currentScene[f.path] ? (
                          <button type="button" data-testid={`enlarge-video-frame-${f.type}`} onClick={() => setLightbox({ url: getImageUrl(currentScene[f.path]), title: f.label, name: `${currentScene.title || 'scene'}-${f.type}.jpg` })}
                            className="relative w-full h-full block cursor-zoom-in">
                            <img src={getImageUrl(currentScene[f.path])} alt="" className="w-full h-full object-cover" />
                            <span className="absolute inset-0 hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <span className="flex items-center gap-1 text-[10px] font-medium text-white bg-black/50 px-2 py-1 rounded"><Maximize2 className="w-3 h-3" /> Enlarge</span>
                            </span>
                          </button>
                        ) : <div className="w-full h-full flex items-center justify-center text-[#55555C]"><ImageIcon className="w-5 h-5" /></div>}
                        <div className="absolute top-2 right-2 z-20 opacity-80 hover:opacity-100 transition-opacity">
                          <FrameEditor projectId={projectId} sceneId={currentScene.id} which={f.type} scene={currentScene} imageProvider={imageProvider}
                            onUpdated={(s) => { setCurrentScene(s); setScenes(prev => prev.map(x => x.id === s.id ? s : x)); }} refreshJobs={refreshJobs} align="right"
                            renderTrigger={({ toggle }) => (
                              <button data-testid={`video-frame-edit-${f.type}`} onClick={toggle}
                                className="flex items-center gap-1 text-[10px] font-medium text-white bg-black/60 hover:bg-black/80 px-2 py-1 rounded transition-colors"><Pencil className="w-3 h-3" /> Edit</button>
                            )} />
                        </div>
                      </div>
                      <div className="px-2 py-1"><span className="text-[9px] tracking-[0.15em] uppercase font-semibold" style={{ color: f.color }}>{f.label}</span></div>
                    </div>
                  ))}
                </div>

                {/* Prompt */}
                <div className="ff-card p-5" data-testid="video-prompt-card">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs tracking-[0.2em] uppercase font-semibold text-[#55555C] flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Video Prompt</span>
                    <div className="flex items-center gap-2">
                      {!editingPrompt && (
                        <>
                          <button data-testid="gen-video-prompt-btn" onClick={handleGeneratePrompt} disabled={genPromptLoading} className="text-xs text-[#FF4500] flex items-center gap-1 disabled:opacity-40" title={simple ? 'Draft a prompt with AI from your frame descriptions' : 'Draft a prompt with AI from the scene, cast and location'}>
                            {genPromptLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {videoPrompt ? 'Regenerate with AI' : 'Generate with AI'}
                          </button>
                          {videoPrompt && (
                            <>
                              <button data-testid="edit-video-prompt-btn" onClick={() => setEditingPrompt(true)} className="text-xs text-[#8A8A93] flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                              <button onClick={() => { navigator.clipboard.writeText(videoPrompt); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                                className="text-xs text-[#00F0FF] flex items-center gap-1">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}</button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {editingPrompt ? (
                    <div>
                      <textarea data-testid="video-prompt-textarea" value={videoPrompt} onChange={e => setVideoPrompt(e.target.value)} className="ff-input w-full h-40 resize-y text-sm font-mono"
                        placeholder={simple ? 'Describe the whole clip — who or what is on screen, the setting, the action, camera movement, lighting and mood. Or click "Generate with AI".' : 'Write the video prompt yourself, or click "Generate with AI" to draft one from your scene, cast and location.'} />
                      <div className="flex gap-2 mt-2 items-center">
                        <button data-testid="save-video-prompt-btn" onClick={handleSavePrompt} disabled={!videoPrompt.trim()} className="text-xs text-[#FF4500] flex items-center gap-1 disabled:opacity-40"><Save className="w-3.5 h-3.5" /> Save</button>
                        {editingPrompt && !!currentScene?.video_prompt && <button onClick={() => { setEditingPrompt(false); setVideoPrompt(currentScene?.video_prompt || ''); }} className="text-xs text-[#8A8A93] flex items-center gap-1"><X className="w-3.5 h-3.5" /> Cancel</button>}
                        {!currentScene?.video_prompt && <span className="text-[10px] text-[#55555C]">Write your own, or generate a draft and refine it.</span>}
                      </div>
                    </div>
                  ) : (
                    <pre className="text-xs text-[#c8c8d0] whitespace-pre-wrap font-mono leading-relaxed bg-[#0a0a0e] rounded-lg p-3 border border-white/[0.04] max-h-[200px] overflow-y-auto">{videoPrompt}</pre>
                  )}
                </div>

                {/* Video Generation */}
                <div className="ff-card p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#E879F9] flex items-center gap-1.5"><Video className="w-3.5 h-3.5" /> Generate Video</p>
                    {(videoProvider === 'kling' || videoProvider === 'seedance') && <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20 flex items-center gap-1"><Volume2 className="w-2.5 h-2.5" /> Native Audio</span>}
                  </div>
                  <VideoResult projectId={projectId} sceneId={currentScene.id} scene={currentScene} fallbackPath={videoStatus?.video_storage_path}
                    characters={characters} onCharacterUpdated={(c) => setCharacters(prev => prev.map(x => x.id === c.id ? c : x))}
                    onUpdated={(s) => { setCurrentScene(s); setScenes(prev => prev.map(x => x.id === s.id ? s : x)); }} />

                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex gap-1">
                      {[{ id: 'kling', label: 'Kling 3.0' }, { id: 'seedance', label: 'Seedance 2.5' }, { id: 'veo', label: 'Veo 3.1' }].map(p => (
                        <button key={p.id} data-testid={`provider-${p.id}`} onClick={() => { setVideoProvider(p.id); setVideoDuration(5); setVideoResolution(r => (p.id === 'kling' && !['720p', '1080p'].includes(r)) ? '720p' : r); }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${videoProvider === p.id ? 'bg-[#E879F9]/15 text-[#E879F9] border border-[#E879F9]/30' : 'text-[#8A8A93] border border-white/5'}`}>{p.label}</button>
                      ))}
                    </div>
                    <select data-testid="duration-select" value={videoDuration} onChange={e => setVideoDuration(Number(e.target.value))} className="ff-input px-2 py-1.5 text-xs rounded-lg">
                      {durationOptions.map(d => <option key={d} value={d}>{d}s</option>)}
                    </select>
                    {(videoProvider === 'seedance' || videoProvider === 'kling') && (
                      <select data-testid="resolution-select" value={videoResolution} onChange={e => setVideoResolution(e.target.value)} className="ff-input px-2 py-1.5 text-xs rounded-lg" title="Output resolution (1080p uses HEVC/H.265 on Seedance — may not preview in all browsers)">
                        {(videoProvider === 'kling' ? ['720p', '1080p'] : ['480p', '720p', '1080p']).map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    )}
                    {simple && (
                      <>
                        <select data-testid="simple-style-select" value={project?.visual_style || 'photorealistic'} onChange={e => updateProjectSetting({ visual_style: e.target.value })} className="ff-input px-2 py-1.5 text-xs rounded-lg" title="Visual style (project-wide)">
                          {VISUAL_STYLES.map(s => <option key={s.id} value={s.id === 'pixel_art' ? (project?.visual_style?.startsWith('pixel_art') ? project.visual_style : 'pixel_art_16bit') : s.id}>{s.label}</option>)}
                        </select>
                        {(project?.visual_style || '').startsWith('pixel_art') && (
                          <select data-testid="simple-pixel-depth-select" value={project.visual_style} onChange={e => updateProjectSetting({ visual_style: e.target.value })} className="ff-input px-2 py-1.5 text-xs rounded-lg">
                            {PIXEL_BIT_DEPTHS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                          </select>
                        )}
                        <select data-testid="simple-aspect-select" value={project?.aspect_ratio || '16:9'} onChange={e => updateProjectSetting({ aspect_ratio: e.target.value })} className="ff-input px-2 py-1.5 text-xs rounded-lg" title="Output format (project-wide)">
                          <option value="16:9">16:9 landscape</option><option value="9:16">9:16 vertical</option><option value="1:1">1:1 square</option>
                        </select>
                      </>
                    )}
                    <button data-testid="gen-video-btn" onClick={handleGenerateVideo} disabled={genVideoLoading || (editingPrompt && videoPrompt !== (currentScene?.video_prompt || ''))}
                      title={editingPrompt && videoPrompt !== (currentScene?.video_prompt || '') ? 'Save the video prompt before generating' : ''}
                      className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium bg-[#E879F9]/15 text-[#E879F9] hover:bg-[#E879F9]/25 border border-[#E879F9]/30 disabled:opacity-40">
                      {genVideoLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                      {genVideoLoading ? 'Generating...' : 'Generate Video'}
                    </button>
                    <span data-testid="cost-video" className="text-[11px] text-[#8A8A93]" title="Approximate estimate based on published provider pricing. You are billed by the provider directly.">
                      Est. cost ~{formatCost(estimateVideoCost(videoProvider, videoDuration, videoResolution, rates))} · {videoDuration}s{(videoProvider === 'seedance' || videoProvider === 'kling') ? ` · ${videoResolution}` : ''}
                    </span>
                  </div>
                  {!simple && <NativeVoiceVideoNotice dialogueLines={dialogueLines.length ? dialogueLines : currentScene.dialogue_lines} characters={characters} provider={videoProvider}
                    preferred={currentScene.native_voice_speakers} onSavePreferred={(ids) => updateCurrentScene({ native_voice_speakers: ids })}
                    onSwitchToKling={() => { setVideoProvider('kling'); setVideoDuration(5); }} />}
                  {!simple && <KlingIdentityNotice scene={currentScene} dialogueLines={dialogueLines.length ? dialogueLines : currentScene.dialogue_lines} characters={characters} provider={videoProvider}
                    enabled={!!project?.kling_identity_lock} saving={savingIdentityLock}
                    onToggle={async (on) => {
                      setSavingIdentityLock(true);
                      try { await api.put(`/projects/${projectId}`, { kling_identity_lock: on }); setProject(p => ({ ...p, kling_identity_lock: on })); toast.success(on ? 'Kling identity locking on for this project' : 'Kling identity locking off'); }
                      catch { toast.error('Could not save setting'); }
                      finally { setSavingIdentityLock(false); }
                    }} />}
                  {videoProvider === 'seedance' && (
                    <div data-testid="seedance-references" className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {[{ kind: 'video', label: 'Reference Video', accept: 'video/*', path: currentScene.reference_video_path, ref: refVideoUploadRef, hint: 'Steers motion & style' },
                        { kind: 'audio', label: 'Reference Audio', accept: 'audio/*', path: currentScene.reference_audio_path, ref: refAudioUploadRef, hint: 'Background music' }].map(r => (
                        <div key={r.kind} className="rounded-lg border border-white/[0.06] bg-[#0f0f13] p-2.5">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-[11px] font-medium text-[#C4B5FD]">{r.label}</p>
                            <span className="text-[9px] text-[#55555C]">{r.hint}</span>
                          </div>
                          <input ref={r.ref} type="file" accept={r.accept} onChange={e => handleUploadReference(e, r.kind)} className="hidden" />
                          {r.path ? (
                            <div className="flex items-center gap-2">
                              <span className="flex-1 text-[10px] text-[#10B981] flex items-center gap-1 truncate"><Check className="w-3 h-3" /> Attached</span>
                              <button data-testid={`replace-ref-${r.kind}`} onClick={() => r.ref.current?.click()} disabled={uploadingRef === r.kind}
                                className="text-[10px] px-2 py-1 rounded bg-white/[0.04] text-[#8A8A93] hover:text-white disabled:opacity-40">Replace</button>
                              <button data-testid={`remove-ref-${r.kind}`} onClick={() => handleDeleteReference(r.kind)}
                                className="text-[10px] px-2 py-1 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20">Remove</button>
                            </div>
                          ) : (
                            <button data-testid={`upload-ref-${r.kind}`} onClick={() => r.ref.current?.click()} disabled={uploadingRef === r.kind}
                              className="w-full text-[10px] py-1.5 rounded bg-[#A78BFA]/10 text-[#A78BFA] hover:bg-[#A78BFA]/20 disabled:opacity-40 flex items-center justify-center gap-1">
                              {uploadingRef === r.kind ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {(() => { const s = suggestDuration(); return s && (
                    <button onClick={() => setVideoDuration(s.seconds)} className={`mt-3 w-full text-left p-2.5 rounded-lg border transition-all ${videoDuration === s.seconds ? 'bg-[#10B981]/5 border-[#10B981]/20' : 'bg-[#F59E0B]/5 border-[#F59E0B]/20 hover:border-[#F59E0B]/40'}`}>
                      <div className="flex items-center gap-2">
                        <Wand2 className={`w-3.5 h-3.5 ${videoDuration === s.seconds ? 'text-[#10B981]' : 'text-[#F59E0B]'}`} />
                        <span className={`text-xs font-medium ${videoDuration === s.seconds ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>
                          {videoDuration === s.seconds ? `${s.seconds}s looks right` : `Suggested: ${s.seconds}s`}
                        </span>
                      </div>
                    </button>
                  ); })()}
                  {genVideoLoading && (
                    <div className="mt-3 space-y-2" data-testid="video-progress">
                      {(() => {
                        const est = PROVIDER_EST_TIMES[videoProvider] || PROVIDER_EST_TIMES.kling;
                        const totalEst = est.base + est.perSec * videoDuration;
                        const pct = Math.min(98, Math.round((elapsedSec / totalEst) * 100));
                        const remaining = Math.max(0, totalEst - elapsedSec);
                        const mins = Math.floor(elapsedSec / 60);
                        const secs = elapsedSec % 60;
                        return (
                          <>
                            <div className="relative h-2 rounded-full bg-white/[0.06] overflow-hidden">
                              <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000 ease-out"
                                style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #E879F9, #FF4500)' }} />
                              <div className="absolute inset-0 rounded-full" style={{ background: 'linear-gradient(90deg, transparent 50%, rgba(255,255,255,0.05) 50%)', backgroundSize: '8px 100%', animation: 'shimmer 1.5s linear infinite' }} />
                            </div>
                            <div className="flex items-center justify-between text-[11px]">
                              <span className="text-[#E879F9] font-medium flex items-center gap-1.5">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                {videoStatus?.progress || 'Generating...'}
                              </span>
                              <div className="flex items-center gap-3">
                                <span className="text-[#8A8A93] tabular-nums">{pct}% &middot; {mins > 0 ? `${mins}m ` : ''}{secs}s elapsed{remaining > 5 ? ` · ~${remaining}s left` : ''}</span>
                                <button onClick={handleCancelVideo} disabled={cancellingVideo} data-testid="cancel-video-btn"
                                  className="text-[10px] px-2 py-0.5 rounded border border-white/15 text-[#C9C9D1] hover:text-white hover:border-white/30 flex items-center gap-1 disabled:opacity-50">
                                  {cancellingVideo ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <X className="w-2.5 h-2.5" />} Cancel
                                </button>
                                {elapsedSec > 180 && (
                                  <button onClick={handleFlushJobs} disabled={flushingJobs} data-testid="reset-stuck-jobs-inline"
                                    className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1">
                                    <RotateCcw className="w-2.5 h-2.5" /> Reset
                                  </button>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {videoStatus?.warnings?.length > 0 && videoStatus.status !== 'failed' && (
                    <div data-testid="video-warning-banner" className="mt-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-xs text-amber-400 font-medium">Rendered with reduced character locking</p>
                        {videoStatus.warnings.map((w, i) => <p key={i} className="text-[11px] text-amber-200/80 mt-0.5 break-words">{w}</p>)}
                      </div>
                    </div>
                  )}
                  {videoStatus?.status === 'cancelled' && (
                    <div data-testid="video-cancelled-banner" className="mt-2 p-3 rounded-lg bg-white/[0.03] border border-white/10 flex items-start gap-2">
                      <X className="w-4 h-4 text-[#8A8A93] shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-xs text-[#C9C9D1] font-medium">Generation cancelled</p>
                        <p className="text-[11px] text-[#8A8A93] mt-0.5 break-words">{videoStatus.error}</p>
                      </div>
                    </div>
                  )}
                  {videoStatus?.status === 'failed' && (
                    <div data-testid="video-error-banner" className="mt-2 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2 min-w-0">
                          <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <p className="text-xs text-red-400 font-medium">Video generation failed</p>
                            <p className="text-[11px] text-red-300/80 mt-0.5 break-words">{videoStatus.error}</p>
                            <p className="text-[11px] text-[#8A8A93] mt-1">{failureHint(videoStatus.error)}</p>
                          </div>
                        </div>
                        <button onClick={handleFlushJobs} disabled={flushingJobs} data-testid="reset-stuck-jobs-failed"
                          className="text-[10px] px-2 py-1 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 flex items-center gap-1 shrink-0">
                          {flushingJobs ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RotateCcw className="w-2.5 h-2.5" />} Reset Jobs
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Lock in & Next Scene */}
                <div className="flex justify-between items-center">
                  <button onClick={() => setWizardStep(3)} className="ff-btn-secondary flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Audio</button>
                  {singleMode ? (
                    <button data-testid="scene-done" onClick={() => navigate(`/project/${projectId}?tab=scenes`)}
                      className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-[#10B981] hover:bg-[#10B981]/80 text-white transition-all">
                      <Check className="w-4 h-4" /> Done — back to Scenes
                    </button>
                  ) : (currentScene.video_path || genVideoLoading) && (
                    !isFinalScene ? (
                      <button data-testid="lock-next-scene" onClick={() => {
                        const msg = currentScene.video_path
                          ? 'Move to the next scene?'
                          : 'This scene is still rendering in the background. Move to the next scene now? You can track the video in the Activity panel.';
                        if (window.confirm(msg)) handleLockAndNext();
                      }}
                        className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-[#FF4500] hover:bg-[#E03C00] text-white transition-all">
                        {currentScene.video_path ? 'Next Scene' : 'Next Scene (keep rendering)'} <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button data-testid="finish-production" onClick={() => navigate(`/project/${projectId}/storyboard`)}
                        className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-[#10B981] hover:bg-[#10B981]/80 text-white transition-all">
                        <Check className="w-4 h-4" /> All Scenes Complete!
                      </button>
                    )
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showLanguages && currentScene && (
        <SceneLanguages projectId={projectId} scene={currentScene} characters={characters} onClose={() => setShowLanguages(false)}
          onScene={(s) => { setCurrentScene(s); setScenes(prev => prev.map(x => x.id === s.id ? s : x)); }} />
      )}
      {linkChar && (
        <LinkExistingPicker projectId={projectId} character={linkChar} onClose={() => setLinkChar(null)}
          onLinked={(u) => setCharacters(prev => prev.map(x => x.id === u.id ? { ...x, ...u } : x))} />
      )}
      {showAddChar && currentScene && (
        <EntityPicker kind="character" projectId={projectId} excludeIds={currentScene.character_ids || []} onClose={() => setShowAddChar(false)}
          onPick={async (c) => {
            setCharacters(prev => prev.some(x => x.id === c.id) ? prev : [...prev, c]);
            const updated = await updateCurrentScene({ character_ids: [...(currentScene.character_ids || []), c.id] });
            if (updated) { setCharIndex(Math.max(0, (updated.character_ids || []).length - 1)); toast.success(`${c.name} added to this scene`); }
          }} />
      )}

      {/* ── Unlock Warning Modal ── */}
      {showUnlockWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="unlock-warning-modal">
          <div className="bg-[#121216] border border-white/10 rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center"><Unlock className="w-5 h-5 text-[#F59E0B]" /></div>
              <h3 className="text-base font-semibold text-white">Unlock Character?</h3>
            </div>
            <p className="text-sm text-[#8A8A93] mb-6">Regenerating this character's portrait may affect consistency with previously generated scenes that used this image.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowUnlockWarning(null)} className="flex-1 py-2 rounded-lg bg-white/5 text-[#8A8A93] text-sm font-medium hover:bg-white/10">Cancel</button>
              <button data-testid="confirm-unlock" onClick={() => { setCharLocked(prev => ({ ...prev, [showUnlockWarning]: false })); setShowUnlockWarning(null); }}
                className="flex-1 py-2 rounded-lg bg-[#F59E0B] text-white text-sm font-medium hover:bg-[#F59E0B]/80">Unlock & Edit</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Missing Key Modal ── */}
      {missingKeys && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="missing-key-modal">
          <div className="bg-[#121216] border border-white/10 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center"><Key className="w-5 h-5 text-[#F59E0B]" /></div>
                <div><h3 className="text-base font-semibold text-white">API Key Required</h3><p className="text-xs text-[#8A8A93]">{missingKeys.message}</p></div>
              </div>
            </div>
            <div className="px-6 pb-4 space-y-3">
              {missingKeys.keys.map(keyName => {
                const meta = KEY_LABELS[keyName] || { label: keyName, url: '' };
                return (
                  <div key={keyName}>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-medium text-[#8A8A93]">{meta.label}</label>
                      {meta.url && <a href={meta.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#F59E0B] flex items-center gap-1">Get key <ExternalLink className="w-2.5 h-2.5" /></a>}
                    </div>
                    <div className="relative">
                      <input data-testid={`inline-key-input-${keyName}`} type={showInlineKeys[keyName] ? 'text' : 'password'}
                        value={inlineKeyValues[keyName] || ''} onChange={e => setInlineKeyValues(prev => ({ ...prev, [keyName]: e.target.value }))}
                        className="ff-input w-full pr-10 text-sm" placeholder={`Paste your ${meta.label}`} autoFocus={missingKeys.keys[0] === keyName} />
                      <button type="button" onClick={() => setShowInlineKeys(prev => ({ ...prev, [keyName]: !prev[keyName] }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#55555C] hover:text-white">
                        {showInlineKeys[keyName] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-4 bg-[#0E0E12] border-t border-white/[0.06] flex items-center justify-between">
              <button onClick={() => { setMissingKeys(null); setInlineKeyValues({}); setShowInlineKeys({}); }} className="text-xs text-[#55555C] hover:text-[#8A8A93]">Cancel</button>
              <button data-testid="save-inline-key-btn" onClick={handleSaveInlineKeys}
                disabled={savingInlineKey || !Object.values(inlineKeyValues).some(v => v?.trim())}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-[#FF4500] hover:bg-[#E03C00] text-white disabled:opacity-40">
                {savingInlineKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />} Save & Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: swipeable bottom step bar */}
      {currentScene && !identifyingScene && (
        <nav data-testid="mobile-step-bar" className="md:hidden fixed bottom-16 inset-x-0 z-[55] bg-[#0d0d11]/95 backdrop-blur-xl border-t border-white/[0.06] px-2 py-1.5">
          <div className="flex items-stretch gap-1 overflow-x-auto snap-x snap-mandatory scrollbar-hide">
            {visibleSteps.map((st) => {
              const i = WIZARD_STEPS.findIndex(w => w.key === st.key);
              const Icon = st.icon; const active = i === wizardStep; const done = i < wizardStep;
              return (
                <button key={st.key} data-testid={`mobile-step-${i}`} onClick={() => setWizardStep(i)}
                  className={`snap-center shrink-0 min-w-[68px] flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-lg text-[10px] transition-colors ${active ? 'bg-[#FF4500] text-white' : done ? 'text-[#10B981]' : 'text-[#8A8A93]'}`}>
                  <Icon className="w-4 h-4" />
                  <span className="truncate max-w-[72px]">{st.label.replace('Scene & Location', 'Location')}</span>
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
